import React, { PropsWithChildren, useContext } from 'react';
import bem from '../../../lib/bemUtils/bem';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { TableBodyContext } from './TableBody.context';

export enum CellRole {
  ROW_HEADER = 'rowheader',
  COLUMN_HEADER = 'columnheader',
}

export enum CellScope {
  COLUMN = 'col',
  ROW = 'row',
}

export enum TableCellModifier {
  XXS_WIDTH = 'xxs-width',
  S_WIDTH = 's-width',
  M_WIDTH = 'm-width',
  L_WIDTH = 'l-width',
  XL_WIDTH = 'xl-width',
  WIDTH_140 = 'width-140',
  WIDTH_170 = 'width-170',
  WIDTH_150 = 'width-150',
  WIDTH_190 = 'width-190',
  WIDTH_23_100 = 'width-23-100',
  LOWPADDING = 'lowpadding',
  LOWPADDING_VERTICAL_CENTER = 'lowpadding-vertical-center',
  POSTIION_RELATIVE = 'postiion-relative',
  ALIGN_RIGHT = 'align-right',
  DARK_BORDER = 'dark-border',
}

export type TableCell = {
  dataHead?: string;
  className?: string;
  id?: string;
  isTableHead?: boolean;
  isTableData?: boolean;
  scope?: CellScope;
  modifier?: Modifier<TableCellModifier>;
  screenreaderOnly?: boolean;
  colSpan?: number;
  ariaSort?: 'descending' | 'ascending' | 'other';
  customTableHeaderClass?: string;
  customTableDataClass?: string;
};

/**
 * Renders a th or td
 * depending on context a th is rendered in a thead
 * in some special cases this can be overwritten via isTableData (mixed th)
 */
export function TableCell({
  colSpan,
  children,
  dataHead,
  id,
  isTableHead,
  isTableData,
  scope,
  screenreaderOnly,
  modifier,
  className = '',
  ariaSort,
  customTableHeaderClass,
  customTableDataClass,
}: PropsWithChildren<TableCell>) {
  const { isTableBody } = useContext(TableBodyContext);
  const cellClass = customTableHeaderClass || `${bem('tables__tableheader', modifier)} ${className}`.trim();

  return !isTableData && (!isTableBody || isTableHead) ? (
    <th scope={scope || CellScope.COLUMN} id={id} colSpan={colSpan} className={cellClass} aria-sort={ariaSort}>
      {screenreaderOnly ? <span className="invisible">{children}</span> : children}
    </th>
  ) : (
    <td
      id={id}
      colSpan={colSpan}
      className={customTableDataClass || `${bem('tables__tabledata', modifier)} ${className}`.trim()}
      data-head={dataHead}
    >
      {screenreaderOnly ? <span className="invisible">{children}</span> : children}
    </td>
  );
}
